import { Injectable, OnDestroy } from '@angular/core';
import { EfficiencyAnalysisApiService } from '@app/shared/services/api/efficiency-analysis-api.service';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, filter, mergeAll, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { IRawWizardData } from '@app/efficiency-analysis/models/raw-wizard-data';
import { IRawHeatData } from '@app/efficiency-analysis/models/raw-heat-data';
import { EfficiencyAnalysisDto } from '@app/shared/models/efficiency-analysis/efficiency-analysis-dto';
import { EfficiencyAnalysisMeters } from '@app/shared/models/efficiency-analysis/efficiency-analysis-meters';
import { Task } from '@app/efficiency-analysis/models/heat-task';
import { EABuilding } from '@app/efficiency-analysis/models/buildings/efficiency-analysis-buildings';
import { isEmpty, isNil } from 'lodash';
import { IBuildingEfficiencyData } from '@app/efficiency-analysis/models/buildings/building-efficiency-data';
import { IHeatData } from '@app/efficiency-analysis/models/heat-data';
import { MeterView } from '@app/shared/models/meter-data/meter-view';

@Injectable({
  providedIn: 'root',
})
export class BuildingEfficiencyWizardStore implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  private _wizardData: BehaviorSubject<IRawWizardData | null> = new BehaviorSubject<IRawWizardData | null>(null);
  private _heatData: BehaviorSubject<IRawHeatData | null> = new BehaviorSubject<IRawHeatData | null>(null);
  private _metersData: BehaviorSubject<MeterView[]> = new BehaviorSubject<MeterView[]>([]);
  private _buildingStore: BehaviorSubject<EABuilding> = new BehaviorSubject<EABuilding>({} as EABuilding);
  private _efficiencyAnalysisStore: BehaviorSubject<EfficiencyAnalysisDto> = new BehaviorSubject<EfficiencyAnalysisDto>({} as EfficiencyAnalysisDto);
  private _efficiencyAnalysisMetersStore: BehaviorSubject<EfficiencyAnalysisMeters[]> = new BehaviorSubject<EfficiencyAnalysisMeters[]>([]);
  private isComputedValuesChangedStore: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public heatSelections: Task[] = [];

  constructor(private efficiencyAnalysisApi: EfficiencyAnalysisApiService, private userMunicipalitiesService: UserMunicipalitiesService) {
    const municipalityKey = this.userMunicipalitiesService.currentMunicipality.key;
    this.building$
      .pipe(
        filter((building: EABuilding) => !isNil(building.guid) && !isEmpty(building.guid)),
        mergeMap((building: EABuilding) => this.efficiencyAnalysisApi.getByBuildingId(building.guid, municipalityKey).pipe(mergeAll())),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((analysis: EfficiencyAnalysisDto) => this._efficiencyAnalysisStore.next(analysis));

    this.heatData$
      .pipe(
        filter((heat: IRawHeatData | null): heat is IRawHeatData => !isNil(heat)),
        mergeMap((heat: IRawHeatData) =>
          this._wizardData.pipe(
            filter((wizard: IRawWizardData | null): wizard is IRawWizardData => !isNil(wizard)),
            tap((wizard: IRawWizardData) => this.updateHeatConsumptions(heat, wizard.buildingEfficiencyData))
          )
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.efficiencyAnalysis$
      .pipe(
        filter((analysis: EfficiencyAnalysisDto) => !isNil(analysis.guid) && !isEmpty(analysis.guid)),
        mergeMap((analysis: EfficiencyAnalysisDto) =>
          this.efficiencyAnalysisApi
            .getEfficiencyAnalysisMetersByEfficiencyAnalysisId(analysis.guid, this.userMunicipalitiesService.currentMunicipality.key)
            .pipe(catchError(() => of([])))
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((meters: EfficiencyAnalysisMeters[]) => this._efficiencyAnalysisMetersStore.next(meters));
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public readonly wizardData$: Observable<IRawWizardData | null> = this._wizardData.asObservable();

  public readonly meters$: Observable<MeterView[]> = this._metersData.asObservable();

  public readonly heatData$: Observable<IRawHeatData | null> = this._heatData.asObservable();

  public readonly building$: Observable<EABuilding> = this._buildingStore.asObservable();

  public readonly efficiencyAnalysis$: Observable<EfficiencyAnalysisDto> = this._efficiencyAnalysisStore.asObservable();

  public readonly efficiencyAnalysisMeters$: Observable<EfficiencyAnalysisMeters[]> = this._efficiencyAnalysisMetersStore.asObservable();

  public readonly isComputedValuesChanged$: Observable<boolean> = this.isComputedValuesChangedStore.asObservable();

  public get electirictyWizardData(): IRawWizardData | null {
    return this._wizardData.getValue();
  }

  public update(electricityWizardData: IRawWizardData | null): void {
    this._wizardData.next(electricityWizardData);
  }

  public clear(): void {
    this._wizardData.next(null);
    this._metersData.next([]);
    this._heatData.next(null);
    this._buildingStore.next({} as EABuilding);
    this._efficiencyAnalysisStore.next({} as EfficiencyAnalysisDto);
    this._efficiencyAnalysisMetersStore.next([]);
    this.isComputedValuesChangedStore.next(false);
  }

  public updateHeat = (heat: IRawHeatData): void => this._heatData.next(heat);

  public updateMeters = (meters: MeterView[]): void => this._metersData.next(meters);

  public saveEfficiencyAnalysis = (analysis: Partial<EfficiencyAnalysisDto>, municipalityKey: string): Observable<EfficiencyAnalysisDto> =>
    this.efficiencyAnalysisApi.saveEfficiencyAnalysis(analysis, municipalityKey);

  public setBuilding = (building: EABuilding): void => this._buildingStore.next(building);

  public saveEfficiencyAnalysisMeter = (meter: Partial<EfficiencyAnalysisMeters>, municipalityKey: string): Observable<EfficiencyAnalysisMeters> =>
    this.efficiencyAnalysisApi.saveEfficiencyAnalysisMeter(meter, municipalityKey);

  public setIsComputedValuesChanged = (value: boolean): void => this.isComputedValuesChangedStore.next(value);

  private updateHeatConsumptions(heat: IRawHeatData, efficiencyAnalysis: IBuildingEfficiencyData): void {
    heat.heatData.forEach((data: IHeatData) => {
      switch (data.energyTypeName?.trim()) {
        case `Strom`:
          efficiencyAnalysis.electricityHeatConsumption = data.totalConsumption;
          break;
        case `Gas`:
          efficiencyAnalysis.naturalGasConsumption = data.totalConsumption;
          break;
        case `Heizol`:
          efficiencyAnalysis.heatingOilConsumption = data.totalConsumption;
          break;
        case `Flüssiggas`:
          efficiencyAnalysis.lpgConsumption = data.totalConsumption;
          break;
        case `Fernwärme`:
          efficiencyAnalysis.districtHeatingConsumption = data.totalConsumption;
          break;
        case `Pellet/Holz`:
          efficiencyAnalysis.pelletWoodConsumption = data.totalConsumption;
          break;
        default:
          break;
      }
    });
  }
}
